import clsx from 'clsx';
import Button from 'components/atoms/Button/Button';
import Image from 'components/atoms/Image/Image';
import PageMetaData from 'components/atoms/PageMetaData/PageMetaData';
import SuspenseWithIndicator from 'components/atoms/SuspenseWithIndicator/SuspendWithIndicator';
import tagConfigs from 'components/atoms/Tag/tagConfigs';
import TagDescription from 'components/atoms/Tag/TagDescription';
import { useIsLoggedIn } from 'components/templates/AuthenticationProvider/AuthenticationProvider';
import { useRouter } from 'next/router';
import React from 'react';
import { graphql } from 'react-relay';
import { useLazyLoadQuery } from 'react-relay/hooks';
import A from '../../atoms/A/A';
import Txt from '../../atoms/Txt/Txt';
import ShareButtons from '../../molecules/ShareButtons/ShareButtons';
import AsideEntryForm from '../../organisms/AsideEntryForm/AsideEntryForm';
import NotFound404 from '../NotFound404/NotFound404';
import * as pages from '../pages';
import OfferDeclineReasons from './CashbackDeclineReasons';
import classes from './OfferLandingPage.module.scss';
import {
  OfferLandingPageQuery,
  OfferLandingPageQuery$data,
} from './__generated__/OfferLandingPageQuery.graphql';

export interface Props {}

const DynamicOfferCta = React.lazy(() => import('./OfferCta'));

export const QUERY = graphql`
  query OfferLandingPageQuery($input: OfferQueryInput!) {
    offer(input: $input) {
      title
      imageUrl
      desktopImageUrl
      description
      guid
      advertiser {
        name
        imageUrl
      }
      categories {
        tag
        name
      }
      rewardLabel
      paysReward
      terms
      tags
      locales
    }
  }
`;

function extractCategories(offer: OfferLandingPageQuery$data['offer']) {
  if (!offer) {
    return [];
  }
  return [...offer.categories].sort((a, b) => a.name.localeCompare(b.name));
}

function constructOfferUrl(guid: string, isCashback: boolean | undefined) {
  if (guid) {
    return `https://www.qmee.com/${isCashback ? 'cashback' : 'deals'}/${guid}`;
  }
  return 'https:/www.qmee.com';
}

const OfferLandingPage: React.FC<Props> = () => {
  const router = useRouter();
  const offerGuid = Array.isArray(router.query.offerGuid)
    ? router.query.offerGuid[0]
    : router.query.offerGuid;
  const data = useLazyLoadQuery<OfferLandingPageQuery>(
    QUERY,
    {
      input: {
        offerGuid: offerGuid || 'missing',
      },
    },
    {
      fetchPolicy: 'store-or-network',
    },
  );

  const isLoggedIn = useIsLoggedIn();
  const isCashback =
    router.pathname.startsWith(pages.CASHBACK.path) || data.offer?.paysReward || false;
  const categories = React.useMemo(() => extractCategories(data.offer), [data]);
  const offersUrl = isCashback ? pages.CASHBACK.path : pages.DEALS.path;

  if (!data.offer) {
    return (
      <NotFound404 subject={isCashback ? 'cashback offer' : 'deal'}>
        <A to={offersUrl}>{isCashback ? 'Cashback offers' : 'Deals'}</A>
      </NotFound404>
    );
  }
  return (
    <AsideEntryForm>
      <div className={classes.root}>
        {data.offer && (
          <PageMetaData
            locales={[...data.offer.locales]}
            metaData={{
              title: data.offer.advertiser?.name || data.offer.title,
              description: data.offer.description || data.offer.title,
              imageUrl: data.offer?.advertiser?.imageUrl || undefined,
              isTranslated: true,
            }}
          />
        )}
        {data.offer?.imageUrl && (
          <div className={classes.bannerContainer}>
            <Image
              priority
              width={640}
              height={260}
              objectFit="cover"
              layout="responsive"
              quality={100}
              className={classes.bannerMobile}
              src={data.offer?.imageUrl || ''}
              alt="Decoration for offer"
            />
            <Image
              priority
              width={1280}
              height={364}
              layout="responsive"
              objectFit="cover"
              quality={100}
              className={classes.bannerDesktop}
              src={data.offer?.desktopImageUrl || data.offer?.imageUrl || ''}
              alt="Decoration for offer"
            />
          </div>
        )}
        <div className={classes.content}>
          <div className={classes.topOverlay}>
            {data.offer?.advertiser?.imageUrl && (
              <div className={classes.advertiserLogoContainer}>
                <div className={classes.advertiserLogo}>
                  <Image
                    priority
                    sizes="30vw"
                    layout="fill"
                    style={{ padding: '1rem' }}
                    objectFit="contain"
                    objectPosition={'center'}
                    src={data.offer?.advertiser?.imageUrl || ''}
                    alt={
                      data.offer?.advertiser?.name
                        ? `${data.offer?.advertiser?.name} logo`
                        : 'Advertiser logo'
                    }
                  />
                </div>
              </div>
            )}
            <ShareButtons
              className={classes.shareButtons}
              type="noTemplate"
              shareInfo={{
                title: 'Check out this Qmee offer!',
                description: 'Check out this cashback offer from Qmee!',
                url: constructOfferUrl(data.offer.guid, isCashback),
                imageUrl: '',
                ref: '',
              }}
            />
          </div>
          <section className={classes.body}>
            <div className={classes.offerSummary}>
              <div className={classes.titleContainer}>
                <h4 className={classes.advertiserName}>{data.offer?.advertiser?.name}</h4>
                {data.offer?.rewardLabel ? (
                  <Txt
                    component="h3"
                    className={clsx(classes.rewardLabel)}
                    translateOptions={{ vars: [data.offer?.rewardLabel || ''] }}
                  >
                    {`Earn %1s;${data.offer.tags.includes('gaming') ? '!' : ' Cashback'}`}
                  </Txt>
                ) : null}
                <Txt component="h1" skipTranslation className={clsx(classes.title)}>
                  {data.offer?.title}
                </Txt>
              </div>
              {data.offer?.description ? (
                <Txt asMarkdown skipTranslation className={classes.description}>
                  {data.offer.description}
                </Txt>
              ) : null}
              {data.offer.tags.find((tag) => tagConfigs[isCashback ? 'cashback' : 'deal'][tag]) ? (
                <div className={classes.offerTags}>
                  {data.offer?.tags.map((tag) => (
                    <TagDescription key={tag} tag={tag} type={isCashback ? 'cashback' : 'deal'} />
                  ))}
                </div>
              ) : null}
              <div className={classes.categoryTags}>
                {categories.map((category) => (
                  <A
                    to={
                      category.tag === 'gaming'
                        ? pages.GAMING.path
                        : `${offersUrl}?category=${category.tag}`
                    }
                    className={classes.category}
                    key={category.tag}
                  >
                    {category.name}
                  </A>
                ))}
              </div>
            </div>
            <div className={classes.offerCTAContainer}>
              <SuspenseWithIndicator withoutSSR force={!isLoggedIn} className={'logged-in-only'}>
                {isLoggedIn && offerGuid && (
                  <div className={classes.offerCTA}>
                    <DynamicOfferCta
                      offerGuid={offerGuid}
                      offerTags={data.offer.tags}
                      isCashback={isCashback}
                    />
                  </div>
                )}
              </SuspenseWithIndicator>
              <Button
                buttonType="primary"
                buttonSize="large"
                color="secondary"
                component={A}
                to={pages.SIGN_UP.path}
                className={clsx(classes.externalLink, 'logged-out-only')}
              >
                Sign up to use our {isCashback ? 'cashback offers' : 'deals'}!
              </Button>
            </div>
          </section>
          <aside className={classes.termsContainer}>
            <Txt component="h4" className={classes.heading}>
              Terms &amp; Conditions
            </Txt>
            <div className={classes.terms}>
              {data.offer?.terms ? (
                <Txt asMarkdown skipTranslation>
                  {data.offer.terms}
                </Txt>
              ) : null}
            </div>
            {isCashback && <OfferDeclineReasons />}
          </aside>
        </div>
      </div>
    </AsideEntryForm>
  );
};

export default OfferLandingPage;
